/**
 * Number formatting helpers — D-41.
 *
 * All helpers return the string "—" for null / undefined / NaN so UI code
 * doesn't need to branch on nullish inputs before rendering a tile. Renders
 * look the same whether an endpoint returned 0 requests or hadn't answered
 * yet (TanStack Query data is undefined while pending).
 *
 * Consumers: src/components/usage/* (TopLineMetrics Tiles, chart axes).
 * Recharts axis tick formatters can pass numbers directly.
 */

const USD_FMT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});

const NUM_FMT = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
});

const COMPACT_FMT = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

/** USD with 2 decimals. "$1,234.50" / "$0.21" / "—" on nullish. */
export function formatCurrency(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return USD_FMT.format(n);
}

/** Thousands-separated number, up to 2 fractional digits. "1,234" / "—". */
export function formatNumber(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return NUM_FMT.format(n);
}

/**
 * Compact K/M suffix. "1.2K" / "3.4M". Used in chart axis labels where space
 * is tight.
 */
export function formatCompactNumber(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return COMPACT_FMT.format(n);
}

/**
 * Canonical latency formatter (SSOT). Sub-second → "150ms"; ≥ 1s → "2.8s"
 * (one decimal). One shared implementation so every tab/table/modal renders
 * latency identically — replaces the four divergent local copies that drifted
 * on spacing ("150 ms" vs "150ms") and precision ("2.80s" vs "2.8s" vs "3s").
 */
export function formatLatency(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

/** Shorthand alias matching REQ wording ("latency P50/P95 in ms"). */
export const formatLatencyMs = formatLatency;

/**
 * Canonical per-trace cost formatter (SSOT). LLM trace costs are tiny, so this
 * keeps 3–4 decimals (unlike `formatCurrency`, which is 2dp for totals/KPIs):
 * null/NaN/0 → "—"; < $0.0001 → "< $0.0001"; < $0.01 → 4dp; else 3dp. One
 * shared implementation replacing the near-identical copies in TraceTable /
 * FeedbackTable / FeedbackCard / UsersTrendBreakdownModal.
 */
export function formatTraceCost(usd: number | null | undefined): string {
  if (usd === null || usd === undefined || Number.isNaN(usd) || usd === 0) {
    return '—';
  }
  if (usd < 0.0001) return '< $0.0001';
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(3)}`;
}

/**
 * Canonical 0–5 rating formatter (SSOT): one decimal + ★, "—" on nullish.
 * Used everywhere a rating renders (KPI cards, delta pills, every table) so
 * "4.2★" looks identical across the dashboard — replaces the per-surface
 * variants that drifted to "4★" / "4.0/5" / "4.3".
 */
export function formatRating(rating: number | null | undefined): string {
  if (rating === null || rating === undefined || Number.isNaN(rating)) {
    return '—';
  }
  return `${rating.toFixed(1)}★`;
}

/** Percent with configurable precision. "42.3%" (default) / "42.333%". */
export function formatPercent(
  n: number | null | undefined,
  digits = 1,
): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return `${n.toFixed(digits)}%`;
}
